import { useEffect, useState } from "react";
import { Layout } from "../components/Layout";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";
import { Trash2, Users, ShieldCheck } from "lucide-react";

export default function Admin() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/admin/users")
      .then((r) => setData(r.data))
      .catch((e) => setErr(apiError(e.response?.data?.detail)));
  };
  useEffect(load, []);

  const remove = async (u) => {
    setBusy(true);
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success(`Removed ${u.email}`);
      setPending(null);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString() : "—");

  return (
    <Layout>
      <div className="space-y-6" data-testid="admin-page">
        <div>
          <div className="label-mono flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> Admin</div>
          <h1 className="font-head font-bold text-2xl sm:text-3xl mt-1">Manage users</h1>
          <p className="text-sm mt-1" style={{ color: "var(--text-2)" }}>
            Removing a user permanently deletes their account and all their data. This cannot be undone.
          </p>
        </div>

        {err && <div className="pm-card p-4 text-sm" data-testid="admin-error" style={{ color: "var(--danger, #c0392b)" }}>{err}</div>}

        {!data && !err && <div className="label-mono" data-testid="admin-loading">Loading users…</div>}

        {data && (
          <div className="pm-card p-4" data-testid="admin-users-list">
            <div className="label-mono mb-3 flex items-center gap-2">
              <Users className="w-4 h-4" /> {data.total} user{data.total === 1 ? "" : "s"}
            </div>
            <div className="space-y-2">
              {data.users.map((u) => (
                <div key={u.id} className="border p-3" style={{ borderColor: "var(--border-subtle)" }} data-testid={`admin-user-${u.id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="font-semibold text-sm break-all">{u.isGuest ? "Guest user" : u.email}</div>
                      <div className="flex items-center gap-2 flex-wrap mt-1">
                        {u.role === "admin" && <span className="badge" style={{ background: "var(--text)", color: "#fff" }}>admin</span>}
                        <span className="badge" style={{ background: u.paid ? "var(--earn)" : "transparent", color: u.paid ? "#fff" : "var(--text-2)" }}>
                          {u.paid ? (u.plan || "paid") : "free"}
                        </span>
                        <span className="label-mono">{u.authProvider}</span>
                        <span className="label-mono">joined {fmt(u.created_at)}</span>
                        {u.accessExpiresAt && <span className="label-mono">expires {fmt(u.accessExpiresAt)}</span>}
                      </div>
                    </div>
                    {u.role !== "admin" && (
                      pending === u.id ? (
                        <div className="flex items-center gap-2">
                          <span className="text-xs" style={{ color: "var(--text-2)" }}>Delete for good?</span>
                          <button onClick={() => remove(u)} disabled={busy} className="pm-btn text-xs px-3" data-testid={`admin-confirm-delete-${u.id}`}>
                            {busy ? "Removing…" : "Confirm"}
                          </button>
                          <button onClick={() => setPending(null)} disabled={busy} className="pm-btn-ghost text-xs px-3" data-testid={`admin-cancel-delete-${u.id}`}>Cancel</button>
                        </div>
                      ) : (
                        <button onClick={() => setPending(u.id)} className="pm-btn-ghost text-xs px-3 flex items-center gap-1.5 whitespace-nowrap" data-testid={`admin-remove-${u.id}`}>
                          <Trash2 className="w-3.5 h-3.5" /> Remove
                        </button>
                      )
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
